import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addNotification,
  updateNotification,
  removeNotification,
  setListenerActive,
  fetchNotifications,
  markAsRead as markAsReadThunk,
  markAllAsRead as markAllAsReadThunk,
  deleteNotification as deleteNotificationThunk,
  undoDeleteNotification as undoDeleteNotificationThunk,
  selectNotificationPreferences
} from '../store/notificationsSlice';
import { selectCurrentUser as selectAuthUser } from '../../../redux/slices/authSlice';
import notificationService from '../services/notificationService';


/**
 * Main hook for notification management
 */
export const useNotifications = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectAuthUser);
  const notifications = useSelector(state => state.notifications.notifications);
  const unreadCount = useSelector(state => state.notifications.unreadCount);
  const loading = useSelector(state => state.notifications.loading);
  const isListenerActive = useSelector(state => state.notifications.isListenerActive);
  const preferences = useSelector(selectNotificationPreferences);

  // Handle notification updates from the service
  const handleNotificationUpdate = useCallback((type, notification) => {
    switch (type) {
      case 'added':
        // Add to Redux store - let the slice handle deduplication
        dispatch(addNotification(notification));
        break;
      case 'modified':
        dispatch(updateNotification(notification));
        break;
      case 'removed':
        dispatch(removeNotification(notification.id));
        break;
      default:
        console.warn('Unknown notification update type:', type);
    }
  }, [dispatch]);

  // Subscribe to real-time notifications with proper cleanup
  useEffect(() => {
    if (!currentUser?.uid) {
      return;
    }

    // Clean up any existing listeners first
    notificationService.unsubscribeFromNotifications(currentUser.uid);

    console.log('Setting up notification listener for user:', currentUser.uid);

    const unsubscribe = notificationService.subscribeToNotifications(
      currentUser.uid,
      handleNotificationUpdate,
      (error) => {
        console.error('Notification subscription error:', error);
        dispatch(setListenerActive(false));
      }
    );

    dispatch(setListenerActive(true));

    // Cleanup function
    return () => {
      console.log('Cleaning up notification listener for user:', currentUser.uid);
      if (unsubscribe) {
        unsubscribe();
      }
      notificationService.unsubscribeFromNotifications(currentUser.uid);
      dispatch(setListenerActive(false));
    };
  }, [currentUser?.uid, dispatch, handleNotificationUpdate]);

  // Initial fetch - separate effect that only runs once when user changes
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (!currentUser?.uid) {
      hasFetchedRef.current = false;
      return;
    }
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchNotifications({
        userId: currentUser.uid,
        limitCount: 20
      }));
    }
  }, [currentUser?.uid, dispatch]);

  useEffect(() => {
    notificationService.setPreferences(preferences);
  }, [preferences]);

  return {
    notifications,
    unreadCount,
    loading,
    isListenerActive
  };
};

/**
 * Hook for triggering notifications from social interactions
 */
export const useNotificationTriggers = () => {
  const currentUser = useSelector(selectAuthUser);
  const preferences = useSelector(selectNotificationPreferences);

  const isTypeEnabled = useCallback((type) => {
    if (!preferences?.inApp) return false;
    const map = {
      like: 'likes',
      comment: 'comments',
      reply: 'replies',
      follow: 'follows',
      follow_back: 'follows',
      follow_request: 'follows',
      follow_request_accepted: 'follows',
      mention: 'mentions',
      share: null,
      message: 'messages',
      project_invite: 'projects',
      project_invite_accepted: 'projects',
      project_launched: 'projects',
      solution_vote: 'solutions',
      solution_reason: 'solutions',
      solution_like: 'solutions',
      task_assignment: 'projects',
      task_comment: 'projects'
    };
    const key = map[type];
    if (!key) return true;
    return preferences?.types?.[key] !== false;
  }, [preferences]);

  const triggerLikeNotification = useCallback(async (postData, authorId) => {
    if (!currentUser || !authorId || authorId === currentUser.uid) return;
    if (!isTypeEnabled('like')) return;

    try {
      await notificationService.createNotification({
        userId: authorId,
        actorId: currentUser.uid,
        type: 'like',
        postId: postData.id,
        targetType: 'post',
        postTitle: postData.content?.substring(0, 100)
      });
    } catch (error) {
      console.error('Error triggering like notification:', error);
    }
  }, [currentUser, isTypeEnabled]);

  const triggerCommentNotification = useCallback(async (postData, commentData, authorId) => {
    if (!currentUser || !authorId || authorId === currentUser.uid) return;
    if (!isTypeEnabled('comment')) return;

    try {
      await notificationService.createNotification({
        userId: authorId,
        actorId: currentUser.uid,
        type: 'comment',
        postId: postData.id,
        commentId: commentData.id,
        commentText: commentData.content,
        postTitle: postData.content?.substring(0, 100)
      });
    } catch (error) {
      console.error('Error triggering comment notification:', error);
    }
  }, [currentUser, isTypeEnabled]);

  const triggerReplyNotification = useCallback(async (commentData, replyData, commentAuthorId) => {
    if (!currentUser || !commentAuthorId || commentAuthorId === currentUser.uid) return;
    if (!isTypeEnabled('reply')) return;

    try {
      await notificationService.createNotification({
        userId: commentAuthorId,
        actorId: currentUser.uid,
        type: 'reply',
        commentId: commentData.id,
        postId: commentData.postId,
        replyText: replyData.content
      });
    } catch (error) {
      console.error('Error triggering reply notification:', error);
    }
  }, [currentUser, isTypeEnabled]);

  const triggerFollowNotification = useCallback(async (followedUserId) => {
    if (!currentUser || !followedUserId || followedUserId === currentUser.uid) return;
    if (!isTypeEnabled('follow')) return;

    try {
      await notificationService.createNotification({
        userId: followedUserId,
        actorId: currentUser.uid,
        type: 'follow'
      });
    } catch (error) {
      console.error('Error triggering follow notification:', error);
    }
  }, [currentUser, isTypeEnabled]);

  const triggerFollowRequestNotification = useCallback(async (followedUserId, followRequestId) => {
    if (!currentUser || !followedUserId || followedUserId === currentUser.uid) return;
    if (!isTypeEnabled('follow_request')) return;

    try {
      await notificationService.createNotification({
        userId: followedUserId,
        actorId: currentUser.uid,
        type: 'follow_request',
        followRequestId: followRequestId || null
      });
    } catch (error) {
      console.error('Error triggering follow request notification:', error);
    }
  }, [currentUser, isTypeEnabled]);

  /**
   * Trigger notification when someone follows you back (mutual follow)
   */
  const triggerFollowBackNotification = useCallback(async (followedBackUserId) => {
    if (!currentUser || !followedBackUserId || followedBackUserId === currentUser.uid) return;
    if (!isTypeEnabled('follow')) return;

    try {
      await notificationService.createNotification({
        userId: followedBackUserId,
        actorId: currentUser.uid,
        type: 'follow_back'
      });
    } catch (error) {
      console.error('Error triggering follow back notification:', error);
    }
  }, [currentUser, isTypeEnabled]);

  const triggerShareNotification = useCallback(async (postData, authorId) => {
    if (!currentUser || !authorId || authorId === currentUser.uid) return;
    if (!isTypeEnabled('share')) return;

    try {
      await notificationService.createNotification({
        userId: authorId,
        actorId: currentUser.uid,
        type: 'share',
        postId: postData.id,
        postTitle: postData.content?.substring(0, 100)
      });
    } catch (error) {
      console.error('Error triggering share notification:', error);
    }
  }, [currentUser, isTypeEnabled]);

  const triggerMentionNotification = useCallback(async (mentionedUserIds, contentData) => {
    if (!currentUser || !mentionedUserIds?.length) return;
    if (!isTypeEnabled('mention')) return;

    try {
      const promises = mentionedUserIds
        .filter(userId => userId !== currentUser.uid)
        .map(userId => notificationService.createNotification({
          userId,
          actorId: currentUser.uid,
          type: 'mention',
          postId: contentData.postId,
          commentId: contentData.commentId,
          targetType: contentData.type || 'post',
          mentionText: contentData.text
        }));

      await Promise.all(promises);
    } catch (error) {
      console.error('Error triggering mention notifications:', error);
    }
  }, [currentUser, isTypeEnabled]);

  const triggerSolutionVoteNotification = useCallback(async (solutionData, voteType, authorId) => {
    if (!currentUser || !authorId || authorId === currentUser.uid) return;
    if (!isTypeEnabled('solution_vote')) return;

    try {
      await notificationService.createNotification({
        userId: authorId,
        actorId: currentUser.uid,
        type: 'solution_vote',
        solutionId: solutionData.id,
        voteType,
        solutionTitle: solutionData.title?.substring(0, 100)
      });
    } catch (error) {
      console.error('Error triggering solution vote notification:', error);
    }
  }, [currentUser, isTypeEnabled]);

  /**
   * Trigger notification when someone likes a solution (Issue #21)
   */
  const triggerSolutionLikeNotification = useCallback(async (solutionData, authorId) => {
    if (!currentUser || !authorId || authorId === currentUser.uid) return;
    if (!isTypeEnabled('solution_like')) return;

    try {
      await notificationService.createNotification({
        userId: authorId,
        actorId: currentUser.uid,
        type: 'like',
        solutionId: solutionData.id,
        solutionTitle: solutionData.title?.substring(0, 100),
        postId: solutionData.postId,
        targetType: 'solution'
      });
    } catch (error) {
      console.error('Error triggering solution like notification:', error);
    }
  }, [currentUser, isTypeEnabled]);

  const triggerMessageNotification = useCallback(async (receiverId, messageText, conversationId) => {
    if (!currentUser || !receiverId || receiverId === currentUser.uid) return;
    if (!isTypeEnabled('message')) return;

    try {
      await notificationService.createNotification({
        userId: receiverId,
        actorId: currentUser.uid,
        type: 'message',
        messageText: messageText?.substring(0, 100),
        conversationId: conversationId || null,
      });
    } catch (error) {
      console.error('Error triggering message notification:', error);
    }
  }, [currentUser, isTypeEnabled]);

  /**
   * Trigger notification when someone adds a reason/perspective to a solution
   */
  const triggerSolutionReasonNotification = useCallback(async (solutionData, reasonType, authorId) => {
    if (!currentUser || !authorId || authorId === currentUser.uid) return;
    if (!isTypeEnabled('solution_reason')) return;

    try {
      await notificationService.createNotification({
        userId: authorId,
        actorId: currentUser.uid,
        type: 'solution_reason',
        solutionId: solutionData.id,
        solutionTitle: solutionData.title?.substring(0, 100),
        reasonType // 'will_work' or 'wont_work'
      });
    } catch (error) {
      console.error('Error triggering solution reason notification:', error);
    }
  }, [currentUser, isTypeEnabled]);

  /**
   * Trigger notification when a user is assigned to a task
   */
  const triggerTaskAssignmentNotification = useCallback(async (taskData, assigneeId) => {
    if (!currentUser || !assigneeId || assigneeId === currentUser.uid) return;
    if (!isTypeEnabled('task_assignment')) return;

    try {
      await notificationService.createNotification({
        userId: assigneeId,
        actorId: currentUser.uid,
        type: 'task_assignment',
        projectId: taskData.projectId,
        projectTitle: taskData.projectTitle?.substring(0, 50),
        taskId: taskData.taskId,
        taskTitle: taskData.taskTitle?.substring(0, 100),
        phaseId: taskData.phaseId
      });
    } catch (error) {
      console.error('Error triggering task assignment notification:', error);
    }
  }, [currentUser, isTypeEnabled]);

  /**
   * Trigger notification when a project is launched (notifies all team members)
   */
  const triggerProjectLaunchNotification = useCallback(async (projectData, teamMemberIds) => {
    if (!currentUser || !Array.isArray(teamMemberIds) || teamMemberIds.length === 0) return;
    if (!isTypeEnabled('project_launched')) return;

    try {
      const promises = teamMemberIds
        .filter(memberId => memberId !== currentUser.uid)
        .map(memberId =>
          notificationService.createNotification({
            userId: memberId,
            actorId: currentUser.uid,
            type: 'project_launched',
            projectId: projectData.id,
            projectTitle: projectData.title?.substring(0, 100)
          })
        );
      await Promise.all(promises);
    } catch (error) {
      console.error('Error triggering project launch notification:', error);
    }
  }, [currentUser, isTypeEnabled]);

  /**
   * Trigger notification for project interactions (like, follow, share)
   */
  const triggerProjectInteractionNotification = useCallback(async (projectData, interactionType, creatorId) => {
    if (!currentUser || !creatorId || creatorId === currentUser.uid) return;
    if (!isTypeEnabled(interactionType)) return;

    try {
      await notificationService.createNotification({
        userId: creatorId,
        actorId: currentUser.uid,
        type: interactionType, // 'project_like', 'project_follow', 'project_share'
        projectId: projectData.id,
        projectTitle: projectData.title?.substring(0, 100)
      });
    } catch (error) {
      console.error('Error triggering project interaction notification:', error);
    }
  }, [currentUser, isTypeEnabled]);

  /**
   * Trigger notification for comment or reply likes
   */
  const triggerCommentLikeNotification = useCallback(async (commentData, authorId) => {
    if (!currentUser || !authorId || authorId === currentUser.uid) return;
    if (!isTypeEnabled('like')) return;

    try {
      await notificationService.createNotification({
        userId: authorId,
        actorId: currentUser.uid,
        type: 'like',
        postId: commentData.postId,
        commentId: commentData.commentId || commentData.id,
        targetType: commentData.isReply ? 'reply' : 'comment',
        contentPreview: commentData.text?.substring(0, 50)
      });
    } catch (error) {
      console.error('Error triggering comment like notification:', error);
    }
  }, [currentUser, isTypeEnabled]);

  return {
    triggerLikeNotification,
    triggerCommentNotification,
    triggerReplyNotification,
    triggerFollowNotification,
    triggerFollowRequestNotification,
    triggerFollowBackNotification,
    triggerShareNotification,
    triggerMentionNotification,
    triggerSolutionVoteNotification,
    triggerMessageNotification,
    // New triggers for Phase 3
    triggerSolutionReasonNotification,
    triggerTaskAssignmentNotification,
    triggerProjectLaunchNotification,
    triggerProjectInteractionNotification,
    triggerCommentLikeNotification,
    triggerSolutionLikeNotification
  };
};

/**
 * Hook for notification actions (mark as read, delete, etc.)
 */
export const useNotificationActions = () => {
  const dispatch = useDispatch();

  const markAsReadAction = useCallback((notificationIds) => {
    const ids = Array.isArray(notificationIds) ? notificationIds : [notificationIds];
    dispatch(markAsReadThunk(ids));
  }, [dispatch]);

  const markAllAsReadAction = useCallback((userId) => {
    dispatch(markAllAsReadThunk(userId));
  }, [dispatch]);

  const deleteNotificationAction = useCallback((notificationId) => {
    dispatch(deleteNotificationThunk(notificationId));

    // Show toast with undo button
    const { toast } = require('react-toastify');
    toast.info(
      ({ closeToast }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span>Notification deleted</span>
          <button
            onClick={() => {
              dispatch(undoDeleteNotificationThunk(notificationId));
              closeToast();
            }}
            style={{
              background: 'none',
              border: '1px solid currentColor',
              padding: '4px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              color: 'inherit',
              fontWeight: 500
            }}
          >
            Undo
          </button>
        </div>
      ),
      {
        position: 'bottom-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: false,
        containerId: 'global-toast-container'
      }
    );
  }, [dispatch]);

  return {
    markAsRead: markAsReadAction,
    markAllAsRead: markAllAsReadAction,
    deleteNotification: deleteNotificationAction
  };
};

/**
 * Hook for desktop notification permissions
 */
export const useDesktopNotifications = () => {
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      return 'not-supported';
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission;
    }

    return Notification.permission;
  }, []);

  const isSupported = 'Notification' in window;
  const permission = isSupported ? Notification.permission : 'not-supported';

  return {
    isSupported,
    permission,
    requestPermission
  };
};
